import { Store } from 'stores/type';
import { TextTutorial } from 'types';
import { delay } from 'utils';
import { TUTORIALS } from './tutorials';

class TextTutorialsManager {
  private $store!: Store;
  private unwatchList: Array<() => void> = [];
  public tutorials: TextTutorial[];

  constructor(tutorials: TextTutorial[]) {
    this.tutorials = tutorials;
  }

  public init(store: Store) {
    this.$store = store;
    this.initWatchers();
  }
  private initWatchers() {
    this.tutorials.forEach((tutorial) => {
      const { id } = tutorial;
      if (this.$store.getters.wasTutorialTextShown(id)) return;

      const unwatch = this.$store.watch(
        (_state, getters) => tutorial.watcher(getters),
        (active) => {
          if (!active) return;

          this.waitToShow(tutorial);
          unwatch();
        },
      );

      this.unwatchList.push(unwatch);
    });
  }
  private async waitToShow(tutorial: TextTutorial) {
    if (typeof tutorial.after === 'function') {
      await tutorial.after(this.$store.getters);
    }

    if (typeof tutorial.delay === 'number') {
      await delay(tutorial.delay);
    }

    // security mechanism for two tutorials to show up after each other (not at the same time)
    await this.canShow();
    this.show(tutorial);
  }
  private canShow(): Promise<void> {
    // show next tutorial only if currently none shown
    if (this.$store.getters.tutorialTextCurrentId === null) return Promise.resolve();

    return new Promise((resolve) => {
      const unwatch = this.$store.watch((_s, getters) => getters.tutorialTextCurrentId === null, (hidden) => {
        if (!hidden) return;

        resolve();
        unwatch();
      });
    });
  }

  public show({ id, getPosition }: TextTutorial) {
    const position = getPosition(this.$store);
    this.$store.dispatch('tutorialTextShow', { id, position });
  }

  public onReset() {
    this.unwatchAll();
    this.initWatchers();
  }
  private unwatchAll() {
    this.unwatchList.forEach((unwatch) => unwatch());
    this.unwatchList = [];
  }

  public getById(tutorialId: TextTutorial['id']) {
    return this.tutorials.find(({ id }) => tutorialId === id) || null;
  }
}

const manager = new TextTutorialsManager(TUTORIALS);
if (__DEV__ || __STAGING__) {
  window.__SHOW_TEXT_TUTORIAL = (tutorial: TextTutorial = TUTORIALS[0]) => manager.show(tutorial);
}

export default manager;
